/**
 * Die Szene dieser App fuer das GlyphField: GlyphLoader (Vollbild-Warten),
 * GlyphErrorPage (404) und `ErrorBoundary appKey` (500). ui-react traegt die
 * Engine NICHT mit; das Modul unten registriert `<wt-glyphfield>` einmal
 * (Nebeneffekt-Import, ~30 KB). Solange es nicht geladen ist, bleibt jedes
 * Feld ein leerer Rahmen — der Text der Bausteine steht trotzdem.
 *
 * **Warum nicht als statischer Import in main.tsx** (so stand es seit 4.2.0):
 * dieselbe Lehre wie in accountingFox (d36c0af2, PR 790). Die Engine zeichnet
 * mit bis zu 30 Bildern je Sekunde auf dem Hauptthread. Laeuft sie schon im
 * ersten Wartebild (`hydrateFallbackElement` in routes.tsx, waehrend der
 * Marketing-Loader laeuft), muss sich der Uebergang zur App zwischen die
 * Bilder draengen; auf einem ausgelasteten CI-Runner kamen Kopfzeile und
 * Launcher dort erst nach der 5-s-Frist der E2E-Pruefung (gemessen in
 * accountingFox: 7,5 s statt 4,5 s mit dem Laden im Leerlauf). Seit dem
 * 09.10.2026 Flottenregel: die Szene wird nie statisch beim Start importiert.
 *
 * **Darum:** die App stoesst das Laden erst an, wenn sie selbst eingehaengt
 * ist (App.tsx, Effekt beim Einhaengen — also waehrend der Sitzungspruefung,
 * nicht davor), und auch dann erst im Leerlauf des Browsers
 * (`requestIdleCallback`). Das Feld laeuft damit, waehrend die App auf das
 * Netz wartet, und nie, waehrend sie die Huelle baut. Ist die Sitzung schnell
 * geprueft, steht die Huelle, bevor die Engine ueberhaupt laedt — 404 und 500
 * bekommen ihr Feld trotzdem, sie liegen in der Huelle.
 *
 * Exakt 4.2.1 bleibt: 4.2.0 erklaerte im design-system nur `*.css` als
 * Nebeneffekt, und der Produktionsbau warf den Szenen-Import weg (leeres Feld).
 */

/** Fuehrt eine Aufgabe spaeter aus — im Test austauschbar. */
export type Planer = (aufgabe: () => void) => void;

/** Im Leerlauf des Browsers; Safari kennt `requestIdleCallback` nicht. */
export const imLeerlauf: Planer = (aufgabe) => {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => aufgabe());
  } else {
    window.setTimeout(aufgabe, 0);
  }
};

let angestossen = false;

/**
 * Laedt die Szene genau einmal, im Leerlauf. Mehrfache Aufrufe sind gefahrlos
 * (StrictMode haengt Effekte doppelt ein). Scheitert das Laden — etwa ein
 * veralteter Chunk nach einem Deploy —, bleibt das Feld leer und der Text steht.
 */
export function ladeGlyphSzeneImLeerlauf(planen: Planer = imLeerlauf): void {
  if (angestossen) return;
  angestossen = true;
  planen(() => {
    import('@weretradeit/design-system/v3/glyphfield/wt-glyphfield.marketShark.js').catch((fehler: unknown) => {
      console.warn('GlyphField-Szene nicht geladen', fehler);
    });
  });
}
